<script>
import { supabase } from "../lib/supabase"
import {
  deleteCategoryBranch,
  deleteStorageByUrl,
  sortRows,
} from "../lib/categoryHierarchy"

export default {
  data() {
    return {
      category: null,
      activeSub: null,
      loading: false,
      error: null,
      actionError: null,
      activeItem: null,
      session: null,
      subObserver: null,
      editingPriceItemId: null,
      editingPriceValue: "",
      savingPrice: false,
      editingItemId: null,
      editingItemDraft: null,
      savingItem: false,
    }
  },
  mounted() {
    this.fetchCategory()
    this.initAuth()
  },
  beforeUnmount() {
    this.destroySubObserver()
  },
  watch: {
    "$route.params.id"() {
      this.fetchCategory()
    },
    activeSub() {
      this.$nextTick(() => {
        this.centerActiveTab()
      })
    },
  },
  computed: {
    isAuthed() {
      return !!this.session
    },
    hasChildCategories() {
      return !!(this.category && this.category.children && this.category.children.length)
    },
    orderedChildCategories() {
      return sortRows((this.category && this.category.children) || [])
    },
    orderedSubcategories() {
      return sortRows((this.category && this.category.subs) || [])
    },
    backTarget() {
      if (this.category && this.category.parent_id) {
        return `/category/${this.category.parent_id}`
      }
      return "/"
    },
    backLabel() {
      if (this.category && this.category.parent_id) {
        return "Üst Kategoriye dön"
      }
      return "Menüye Geri Dön"
    },
    currentEditingItem() {
      if (!this.category || !this.category.subs || !this.editingItemId) return null
      for (const sub of this.category.subs) {
        const match = (sub.items || []).find(item => item.id === this.editingItemId)
        if (match) return match
      }
      return null
    },
  },
  methods: {
    async initAuth() {
      const { data } = await supabase.auth.getSession()
      this.session = data.session
      supabase.auth.onAuthStateChange((_event, session) => {
        this.session = session
      })
    },
    openItem(item) {
      this.activeItem = item
    },
    closeItem() {
      this.activeItem = null
    },
    startItemEdit(item) {
      if (!item || !item.id) return
      this.actionError = null
      this.editingItemId = item.id
      this.editingItemDraft = {
        name: item.name || "",
        description: item.description || "",
        price: item.price ?? 0,
        spice: item.spice ?? 0,
        image: item.image || "",
        sort: item.sort ?? 1,
        subcategory_id: item.subcategory_id || "",
      }
    },
    cancelItemEdit() {
      this.editingItemId = null
      this.editingItemDraft = null
      this.savingItem = false
    },
    async reindexSubcategories(reorderedSubs) {
      const ordered = reorderedSubs || []
      const updates = ordered.map((sub, index) => {
        const target = index + 1
        if ((Number(sub.sort) || 0) === target) return null
        return supabase
          .from("subcategories")
          .update({ sort: target })
          .select("id,sort")
          .eq("id", sub.id)
      }).filter(Boolean)

      if (!updates.length) return true

      const results = await Promise.all(updates)
      const withErr = results.find(result => result.error)
      if (withErr?.error) {
        this.actionError = `Alt kategori sirasi guncellenemedi: ${withErr.error.message}`
        return false
      }

      const noRow = results.find(result => !(result.data && result.data.length))
      if (noRow) {
        this.actionError = "Alt kategori sirasi guncellenemedi: yetki (RLS) sorunu olabilir."
        return false
      }

      return true
    },
    canMoveSubcategory(sub, delta) {
      const ordered = this.orderedSubcategories
      const index = ordered.findIndex(row => row.id === sub.id)
      if (index === -1) return false
      const target = index + delta
      return target >= 0 && target < ordered.length
    },
    async moveSubcategory(sub, delta) {
      const ordered = this.orderedSubcategories
      const index = ordered.findIndex(row => row.id === sub.id)
      if (index === -1) return

      const target = index + delta
      if (target < 0 || target >= ordered.length) return

      const swapped = [...ordered]
      ;[swapped[index], swapped[target]] = [swapped[target], swapped[index]]
      this.actionError = null

      const ok = await this.reindexSubcategories(swapped)
      if (!ok) return
      this.fetchCategory()
    },
    async reindexChildCategories(reorderedCategories) {
      const ordered = reorderedCategories || []
      const updates = ordered.map((category, index) => {
        const target = index + 1
        if ((Number(category.sort) || 0) === target) return null
        return supabase
          .from("categories")
          .update({ sort: target })
          .select("id,sort")
          .eq("id", category.id)
      }).filter(Boolean)

      if (!updates.length) return true

      const results = await Promise.all(updates)
      const withErr = results.find(result => result.error)
      if (withErr?.error) {
        this.actionError = `Alt kategori sayfa sirasi guncellenemedi: ${withErr.error.message}`
        return false
      }

      const noRow = results.find(result => !(result.data && result.data.length))
      if (noRow) {
        this.actionError = "Alt kategori sayfa sirasi guncellenemedi: yetki (RLS) sorunu olabilir."
        return false
      }

      return true
    },
    canMoveChildCategory(category, delta) {
      const ordered = this.orderedChildCategories
      const index = ordered.findIndex(row => row.id === category.id)
      if (index === -1) return false
      const target = index + delta
      return target >= 0 && target < ordered.length
    },
    async moveChildCategory(category, delta) {
      const ordered = this.orderedChildCategories
      const index = ordered.findIndex(row => row.id === category.id)
      if (index === -1) return

      const target = index + delta
      if (target < 0 || target >= ordered.length) return

      const swapped = [...ordered]
      ;[swapped[index], swapped[target]] = [swapped[target], swapped[index]]
      this.actionError = null

      const ok = await this.reindexChildCategories(swapped)
      if (!ok) return
      this.fetchCategory()
    },
    async deleteChildCategory(category) {
      if (!confirm("Alt kategori sayfasi silinsin mi?")) return
      this.actionError = null

      const { error } = await deleteCategoryBranch(supabase, category.id)
      if (error) {
        this.actionError = error
        return
      }

      this.fetchCategory()
    },
    orderedItemsInSub(sub) {
      return sortRows(sub && sub.items ? sub.items : [])
    },
    canMoveItemInSub(sub, item, delta) {
      const ordered = this.orderedItemsInSub(sub)
      const index = ordered.findIndex(row => row.id === item.id)
      if (index === -1) return false
      const target = index + delta
      return target >= 0 && target < ordered.length
    },
    async reindexSubItems(reorderedItems) {
      const ordered = reorderedItems || []
      const updates = ordered.map((item, index) => {
        const target = index + 1
        if ((Number(item.sort) || 0) === target) return null
        return supabase
          .from("items")
          .update({ sort: target })
          .select("id,sort")
          .eq("id", item.id)
      }).filter(Boolean)

      if (!updates.length) return true

      const results = await Promise.all(updates)
      const withErr = results.find(result => result.error)
      if (withErr?.error) {
        this.actionError = `Urun sirasi guncellenemedi: ${withErr.error.message}`
        return false
      }

      const noRow = results.find(result => !(result.data && result.data.length))
      if (noRow) {
        this.actionError = "Urun sirasi guncellenemedi: yetki (RLS) sorunu olabilir."
        return false
      }

      return true
    },
    async moveItemInSub(sub, item, delta) {
      const ordered = this.orderedItemsInSub(sub)
      const index = ordered.findIndex(row => row.id === item.id)
      if (index === -1) return

      const target = index + delta
      if (target < 0 || target >= ordered.length) return

      const swapped = [...ordered]
      ;[swapped[index], swapped[target]] = [swapped[target], swapped[index]]
      this.actionError = null

      const ok = await this.reindexSubItems(swapped)
      if (!ok) return
      this.fetchCategory()
    },
    scrollToSub(sub) {
      if (!sub || !sub.id) return
      this.activeSub = sub
      const el = document.getElementById(`sub-${sub.id}`)
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    },
    centerActiveTab() {
      if (!this.activeSub || !this.activeSub.id || this.hasChildCategories) return
      const wrap = this.$refs.subTabs
      const ref = this.$refs[`tab-${this.activeSub.id}`]
      const btn = Array.isArray(ref) ? ref[0] : ref
      if (!wrap || !btn) return
      const wrapRect = wrap.getBoundingClientRect()
      const btnRect = btn.getBoundingClientRect()
      const current = wrap.scrollLeft
      const delta = (btnRect.left + btnRect.width / 2) - (wrapRect.left + wrapRect.width / 2)
      const target = current + delta
      wrap.scrollTo({ left: target, behavior: "smooth" })
    },
    destroySubObserver() {
      if (this.subObserver) {
        this.subObserver.disconnect()
        this.subObserver = null
      }
    },
    setupSubObserver() {
      this.destroySubObserver()
      if (this.hasChildCategories) return

      const sections = Array.from(document.querySelectorAll(".sub-section"))
      if (!sections.length) return

      this.subObserver = new IntersectionObserver(
        entries => {
          const visible = entries
            .filter(entry => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          if (!visible.length) return

          const id = visible[0].target.getAttribute("id")
          if (!id) return

          const subId = id.replace("sub-", "")
          const match = ((this.category && this.category.subs) || [])
            .find(sub => String(sub.id) === String(subId))

          if (match) {
            this.activeSub = match
          }
        },
        {
          root: null,
          rootMargin: "-80px 0px -70% 0px",
          threshold: [0.1, 0.25, 0.5, 0.75],
        }
      )

      sections.forEach(section => this.subObserver.observe(section))
    },
    async uploadEditingItemImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.editingItemDraft) return

      this.savingItem = true
      this.actionError = null

      const path = `items/${Date.now()}-${file.name}`
      const { error } = await supabase
        .storage
        .from("menu-images")
        .upload(path, file, { cacheControl: "3600", upsert: false })

      this.savingItem = false

      if (error) {
        this.actionError = "Gorsel yuklenemedi."
        return
      }

      const { data } = supabase.storage.from("menu-images").getPublicUrl(path)
      this.editingItemDraft.image = data.publicUrl
    },
    startPriceEdit(item) {
      if (!item || !item.id) return
      this.actionError = null
      this.editingPriceItemId = item.id
      this.editingPriceValue = String(item.price || 0)
    },
    cancelPriceEdit() {
      this.editingPriceItemId = null
      this.editingPriceValue = ""
      this.savingPrice = false
    },
    async saveItemPrice(item) {
      if (!item || !item.id || this.editingPriceItemId !== item.id) return

      this.actionError = null
      const value = Number.parseFloat(this.editingPriceValue)
      if (!Number.isFinite(value) || value < 0) {
        this.actionError = "Gecerli bir fiyat giriniz."
        return
      }

      this.savingPrice = true
      const { error } = await supabase
        .from("items")
        .update({ price: value })
        .eq("id", item.id)
      this.savingPrice = false

      if (error) {
        this.actionError = "Fiyat guncellenemedi."
        return
      }

      if (this.activeItem && this.activeItem.id === item.id) {
        this.activeItem = {
          ...this.activeItem,
          price: value,
        }
      }

      this.cancelPriceEdit()
      this.fetchCategory()
    },
    async saveItemEdit() {
      if (!this.currentEditingItem || !this.editingItemDraft) return

      const draft = this.editingItemDraft
      if (!draft.name || !draft.subcategory_id) {
        this.actionError = "Urun adi ve alt kategori zorunlu."
        return
      }

      this.actionError = null
      this.savingItem = true

      const payload = {
        name: draft.name,
        description: draft.description,
        price: Math.max(0, Number(draft.price) || 0),
        spice: Math.min(5, Math.max(0, Number.parseInt(draft.spice, 10) || 0)),
        image: draft.image,
        sort: Math.max(1, Number.parseInt(draft.sort, 10) || 1),
        subcategory_id: draft.subcategory_id,
      }

      const previousImage = this.currentEditingItem.image
      const { error } = await supabase
        .from("items")
        .update(payload)
        .eq("id", this.currentEditingItem.id)

      this.savingItem = false

      if (error) {
        this.actionError = "Urun guncellenemedi."
        return
      }

      if (previousImage && payload.image && previousImage !== payload.image) {
        const { error: storageError } = await deleteStorageByUrl(supabase, previousImage)
        if (storageError) {
          this.actionError = "Eski gorsel silinemedi."
        }
      }

      if (this.activeItem && this.activeItem.id === this.currentEditingItem.id) {
        this.activeItem = {
          ...this.activeItem,
          ...payload,
        }
      }

      this.cancelItemEdit()
      this.fetchCategory()
    },
    async deleteSubcategory(sub) {
      if (!confirm("Sub kategori silinsin mi?")) return

      for (const item of sub.items || []) {
        const { error: storageError } = await deleteStorageByUrl(supabase, item.image)
        if (storageError) {
          this.error = "Gorseller silinemedi."
          return
        }
      }

      const { error: delItemsErr } = await supabase
        .from("items")
        .delete()
        .eq("subcategory_id", sub.id)

      if (delItemsErr) {
        this.error = "Urunler silinemedi."
        return
      }

      const { error: delSubErr } = await supabase
        .from("subcategories")
        .delete()
        .eq("id", sub.id)

      if (delSubErr) {
        this.error = "Sub kategori silinemedi."
        return
      }

      this.fetchCategory()
    },
    async deleteItem(item) {
      if (!confirm("Urun silinsin mi?")) return

      const { error: storageError } = await deleteStorageByUrl(supabase, item.image)
      if (storageError) {
        this.error = "Gorsel silinemedi."
        return
      }

      const { error: delItemErr } = await supabase
        .from("items")
        .delete()
        .eq("id", item.id)

      if (delItemErr) {
        this.error = "Urun silinemedi."
        return
      }

      this.fetchCategory()
      this.closeItem()
    },
    async fetchCategory() {
      this.loading = true
      this.error = null
      this.actionError = null
      this.category = null
      this.activeSub = null

      const id = this.$route.params.id
      const [
        { data: categoryRows, error: categoryError },
        { data: childRows, error: childError },
        { data: subRows, error: subError },
      ] = await Promise.all([
        supabase
          .from("categories")
          .select("id,title,cover,sort,parent_id")
          .eq("id", id)
          .limit(1),
        supabase
          .from("categories")
          .select("id,title,cover,sort,parent_id")
          .eq("parent_id", id)
          .order("sort", { ascending: true }),
        supabase
          .from("subcategories")
          .select("id,title,sort,category_id")
          .eq("category_id", id)
          .order("sort", { ascending: true }),
      ])

      if (categoryError || childError || subError) {
        this.error = "Kategori yuklenemedi."
        this.loading = false
        return
      }

      const categoryRow = categoryRows && categoryRows[0] ? categoryRows[0] : null
      if (!categoryRow) {
        this.loading = false
        return
      }

      const subIds = (subRows || []).map(sub => sub.id)
      let itemsBySubcategory = {}

      if (subIds.length) {
        const { data: itemRows, error: itemError } = await supabase
          .from("items")
          .select("id,name,description,price,image,sort,spice,subcategory_id")
          .in("subcategory_id", subIds)
          .order("sort", { ascending: true })

        if (itemError) {
          this.error = "Urunler yuklenemedi."
          this.loading = false
          return
        }

        itemsBySubcategory = (itemRows || []).reduce((acc, item) => {
          if (!acc[item.subcategory_id]) acc[item.subcategory_id] = []
          acc[item.subcategory_id].push(item)
          return acc
        }, {})
      }

      this.category = {
        ...categoryRow,
        children: sortRows(childRows || []),
        subs: sortRows(subRows || []).map(sub => ({
          ...sub,
          items: sortRows(itemsBySubcategory[sub.id] || []),
        })),
      }

      if (this.category && !this.category.children.length && this.orderedSubcategories.length) {
        this.activeSub = this.orderedSubcategories[0]
      }

      this.$nextTick(() => {
        this.setupSubObserver()
      })

      this.loading = false
    },
    spiceEmojis(level) {
      const value = Number(level)
      if (!Number.isFinite(value) || value <= 0) return ""
      const count = Math.min(5, Math.max(0, Math.round(value)))
      return "🌶".repeat(count)
    },
    subcategoryLabel(id) {
      const match = this.orderedSubcategories.find(sub => String(sub.id) === String(id))
      return match ? match.title : "-"
    },
  },
}
</script>

<template>
  <div class="category">
    <div class="decor-wrap" aria-hidden="true">
      <div class="decor-top"></div>
      <div class="decor-bottom"></div>
    </div>

    <header class="header">
      <button class="back" @click="$router.push(backTarget)">{{ backLabel }}</button>
      <h1>{{ category ? category.title : "Kategori" }}</h1>
    </header>

    <div v-if="loading" class="state">Yukleniyor...</div>
    <div v-else-if="error" class="state">{{ error }}</div>
    <div v-else-if="actionError" class="state">{{ actionError }}</div>
    <div v-else-if="!category" class="state">Kategori bulunamadi.</div>

    <div v-else-if="hasChildCategories" class="child-categories">
      <div
        v-for="child in orderedChildCategories"
        :key="child.id"
        class="category-card"
        @click="$router.push(`/category/${child.id}`)"
      >
        <img :src="child.cover" :alt="child.title" loading="lazy" decoding="async" />
        <div class="category-order-badges" v-if="isAuthed">
          <button
            class="category-order-badge"
            @click.stop="moveChildCategory(child, -1)"
            :disabled="!canMoveChildCategory(child, -1)"
            aria-label="Ust siraya al"
          >
            ↑
          </button>
          <button
            class="category-order-badge"
            @click.stop="moveChildCategory(child, 1)"
            :disabled="!canMoveChildCategory(child, 1)"
            aria-label="Alt siraya al"
          >
            ↓
          </button>
        </div>
        <button
          v-if="isAuthed"
          class="category-delete-badge"
          @click.stop="deleteChildCategory(child)"
          aria-label="Alt kategori sayfasini sil"
        >
          ×
        </button>
        <div class="category-overlay">
          <h2>{{ child.title }}</h2>
        </div>
      </div>
    </div>

    <template v-else-if="orderedSubcategories.length">
      <div class="sub-tabs" ref="subTabs">
        <div
          v-for="sub in orderedSubcategories"
          :key="sub.id"
          class="sub-pill"
        >
          <button
            :class="{ active: activeSub && sub.id === activeSub.id }"
            :ref="`tab-${sub.id}`"
            @click="scrollToSub(sub)"
          >
            {{ sub.title }}
          </button>
          <button
            v-if="isAuthed"
            class="pill-delete"
            @click.stop="deleteSubcategory(sub)"
            aria-label="Sub kategori sil"
          >
            ×
          </button>
        </div>
      </div>

      <div class="items">
        <section
          v-for="sub in orderedSubcategories"
          :key="sub.id"
          :id="`sub-${sub.id}`"
          class="sub-section"
        >
          <div class="sub-heading">
            <h2>{{ sub.title }}</h2>
            <div class="sub-order" v-if="isAuthed">
              <button
                class="sub-order-btn"
                @click.stop="moveSubcategory(sub, -1)"
                :disabled="!canMoveSubcategory(sub, -1)"
                aria-label="Onceki alt kategoriye al"
              >
                ↑
              </button>
              <button
                class="sub-order-btn"
                @click.stop="moveSubcategory(sub, 1)"
                :disabled="!canMoveSubcategory(sub, 1)"
                aria-label="Sonraki alt kategoriye al"
              >
                ↓
              </button>
            </div>
          </div>

          <div v-if="!sub.items || !sub.items.length" class="empty">
            <div class="empty-card">
              <div class="empty-mark"></div>
              <h3>Bu sub kategoride urun yok.</h3>
              <p>Yeni urun eklemek icin admin panelinden ekleyebilirsin.</p>
            </div>
          </div>

          <div
            v-for="item in orderedItemsInSub(sub)"
            :key="`item-${item.id}`"
            class="item-card"
            @click="openItem(item)"
          >
            <button
              v-if="isAuthed"
              class="item-edit-badge"
              @click.stop="startItemEdit(item)"
              aria-label="Urunu duzenle"
            >
              Duzenle
            </button>

            <div class="item-order" v-if="isAuthed">
              <button
                class="item-order-btn"
                @click.stop="moveItemInSub(sub, item, -1)"
                :disabled="!canMoveItemInSub(sub, item, -1)"
                aria-label="Onceki siraya al"
              >
                ↑
              </button>
              <button
                class="item-order-btn"
                @click.stop="moveItemInSub(sub, item, 1)"
                :disabled="!canMoveItemInSub(sub, item, 1)"
                aria-label="Sonraki siraya al"
              >
                ↓
              </button>
            </div>

            <div class="info">
              <div class="top">
                <div class="top-title">
                  <h3>{{ item.name }}</h3>
                  <p v-if="spiceEmojis(item.spice)" class="spice">{{ spiceEmojis(item.spice) }}</p>
                </div>
                <template v-if="editingPriceItemId === item.id">
                  <span class="price-chip price-editing">
                    <input
                      v-model="editingPriceValue"
                      type="number"
                      min="0"
                      step="0.01"
                      class="price-input"
                      @keyup.enter="saveItemPrice(item)"
                      @keyup.esc="cancelPriceEdit"
                      @click.stop
                    />
                    <button class="price-save" @click.stop="saveItemPrice(item)" :disabled="savingPrice">
                      Kaydet
                    </button>
                    <button class="price-cancel" @click.stop="cancelPriceEdit">
                      Vazgec
                    </button>
                  </span>
                </template>
                <span
                  v-else-if="isAuthed"
                  class="price-chip price-chip-editable"
                  role="button"
                  tabindex="0"
                  @click.stop="startPriceEdit(item)"
                  @keyup.enter.space.prevent="startPriceEdit(item)"
                  aria-label="Fiyati duzenle"
                >
                  {{ item.price }} &#x20BA;
                  <button
                    class="price-edit-trigger"
                    @click.stop="startPriceEdit(item)"
                    aria-label="Fiyati duzenle"
                  >
                    ✎
                  </button>
                </span>
                <span v-else class="price-chip">
                  {{ item.price }} &#x20BA;
                </span>
              </div>

              <p class="description">{{ item.description }}</p>
            </div>

            <div class="thumb">
              <img
                :src="item.image"
                :alt="item.name"
                loading="lazy"
                decoding="async"
                width="120"
                height="90"
              />
              <button
                v-if="isAuthed"
                class="delete-badge"
                @click.stop="deleteItem(item)"
                aria-label="Urunu sil"
              >
                ×
              </button>
            </div>
          </div>
        </section>
      </div>
    </template>

    <div v-else-if="category" class="state">Urun bulunamadi.</div>

    <div
      v-if="isAuthed && editingItemId"
      class="item-edit-overlay"
      @click.self="cancelItemEdit"
    >
      <div class="item-edit-panel">
        <div class="item-edit-header">
          <h3>Kart Bilgilerini Duzenle</h3>
          <button class="item-edit-close" @click="cancelItemEdit">Kapat</button>
        </div>

        <div v-if="editingItemDraft" class="item-edit-form">
          <input v-model="editingItemDraft.name" placeholder="Urun adi" />
          <input v-model="editingItemDraft.description" placeholder="Aciklama" />
          <input v-model="editingItemDraft.price" type="number" min="0" step="0.01" placeholder="Fiyat" />
          <input v-model="editingItemDraft.spice" type="number" min="0" max="5" step="1" placeholder="Acilik (0-5)" />
          <input v-model="editingItemDraft.sort" type="number" min="1" step="1" placeholder="Sira" />
          <select v-model="editingItemDraft.subcategory_id">
            <option value="">Alt kategori sec</option>
            <option
              v-for="option in orderedSubcategories"
              :key="`edit-sub-${option.id}`"
              :value="option.id"
            >
              {{ option.title }}
            </option>
          </select>
          <input v-model="editingItemDraft.image" placeholder="Gorsel URL" />
          <input type="file" @change="uploadEditingItemImage" />
          <div class="item-edit-meta">
            Aktif alt kategori: {{ subcategoryLabel(editingItemDraft.subcategory_id) }}
          </div>
          <div class="item-edit-actions">
            <button class="item-edit-save" @click="saveItemEdit" :disabled="savingItem">
              {{ savingItem ? "Kaydediliyor..." : "Kaydet" }}
            </button>
            <button class="item-edit-cancel" @click="cancelItemEdit">
              Vazgec
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeItem" class="lightbox" @click.self="closeItem">
      <div class="lightbox-card">
        <button class="close" @click="closeItem">Kapat</button>
        <div class="lightbox-image">
          <img
            :src="activeItem.image"
            :alt="activeItem.name"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        </div>
        <div class="lightbox-info">
          <h2>{{ activeItem.name }}</h2>
          <p>{{ activeItem.description }}</p>
          <p v-if="spiceEmojis(activeItem.spice)" class="spice">
            {{ spiceEmojis(activeItem.spice) }}
          </p>
          <div class="price">{{ activeItem.price }} ₺</div>
        </div>
      </div>
    </div>

    <button class="back-fab" @click="$router.push(backTarget)" :aria-label="backLabel">
      {{ backLabel }}
    </button>
  </div>
</template>

<style scoped>
.category {
  padding-bottom: 40px;
  position: relative;
  overflow: visible;
  min-height: 100%;
}

.decor-wrap {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.decor-top,
.decor-bottom {
  position: absolute;
  left: -10%;
  width: 120%;
  z-index: 0;
}

.decor-top {
  top: -30px;
  height: 160px;
  background:
    radial-gradient(100px 50px at 15% 30%, rgba(73, 182, 255, 0.3), transparent 70%),
    radial-gradient(120px 60px at 70% 25%, rgba(47, 123, 255, 0.25), transparent 70%),
    radial-gradient(140px 70px at 85% 60%, rgba(73, 182, 255, 0.2), transparent 70%);
}

.decor-top::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='160' viewBox='0 0 1200 160' fill='none'><g stroke='rgba(73,182,255,0.35)' stroke-width='3' stroke-linecap='round'><path d='M140 50c18-8 40-8 58 0c-18 8-40 8-58 0z'/><circle cx='200' cy='50' r='2' fill='rgba(73,182,255,0.7)'/><path d='M560 35c18-8 40-8 58 0c-18 8-40 8-58 0z'/><circle cx='620' cy='35' r='2' fill='rgba(73,182,255,0.7)'/><path d='M900 60c22-10 48-10 70 0c-22 10-48 10-70 0z'/><circle cx='970' cy='60' r='2' fill='rgba(73,182,255,0.7)'/></g></svg>");
  background-repeat: no-repeat;
  background-position: center top;
  opacity: 0.9;
}

.decor-bottom {
  top: 35%;
  bottom: 0;
  height: auto;
  background:
    radial-gradient(160px 100px at 20% 75%, rgba(11, 35, 66, 0.9), transparent 70%),
    radial-gradient(220px 120px at 80% 70%, rgba(15, 47, 89, 0.9), transparent 70%),
    linear-gradient(180deg, transparent 0%, rgba(6, 18, 37, 0.85) 55%, rgba(6, 18, 37, 1) 100%);
}

.decor-bottom::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='240' viewBox='0 0 1200 240' fill='none'><g stroke='rgba(73,182,255,0.22)' stroke-width='3' stroke-linecap='round'><path d='M160 190c0-20 10-34 26-44' /><path d='M160 190c8-18 22-30 44-38' /><path d='M320 210c0-24 12-42 30-54' /><path d='M320 210c10-20 28-34 54-42' /><path d='M960 200c0-22 12-38 28-50' /><path d='M960 200c10-18 26-30 50-38' /></g><path d='M0 110c100 30 200 30 300 0s200-30 300 0s200 30 300 0s200-30 300 0s200-30 300 0' stroke='rgba(73,182,255,0.12)' stroke-width='2' fill='none'/></svg>");
  background-repeat: repeat-y;
  background-size: 1200px 240px;
  background-position: center top;
  opacity: 0.9;
}

.header {
  padding: 16px;
  color: var(--text);
  background: rgba(6, 18, 37, 0.72);
  position: relative;
  z-index: 1;
}

.back {
  background: transparent;
  color: var(--muted);
  border: 1px solid rgba(73, 182, 255, 0.25);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
}

.back-fab {
  position: fixed;
  right: 16px;
  bottom: 18px;
  z-index: 6;
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #041327;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(2, 10, 24, 0.35);
}

.state {
  color: var(--muted);
  padding: 10px 16px 20px;
  position: relative;
  z-index: 1;
}

.child-categories {
  padding: 0 16px 24px;
  position: relative;
  z-index: 1;
}

.category-card {
  position: relative;
  height: 180px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 18px;
  cursor: pointer;
  border: 1px solid rgba(73, 182, 255, 0.18);
  box-shadow: 0 18px 40px rgba(2, 10, 24, 0.55);
}

.category-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.category-order-badges {
  position: absolute;
  left: 10px;
  top: 10px;
  display: inline-flex;
  gap: 6px;
  z-index: 2;
}

.category-order-badge {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(73, 182, 255, 0.45);
  background: rgba(8, 18, 33, 0.75);
  color: var(--text);
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  padding: 0;
}

.category-order-badge:disabled {
  opacity: 0.4;
  cursor: default;
}

.category-delete-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 92, 92, 0.5);
  background: rgba(8, 18, 33, 0.7);
  color: #ff8a8a;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  z-index: 2;
}

.category-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(6, 18, 37, 0.1),
    rgba(6, 18, 37, 0.75) 55%,
    rgba(7, 22, 45, 0.95)
  );
  display: flex;
  align-items: flex-end;
  padding: 16px;
}

.category-overlay h2 {
  color: var(--text);
  font-size: 24px;
  margin: 0;
}

.sub-tabs {
  display: flex;
  gap: 10px;
  padding: 10px 16px 12px;
  overflow-x: auto;
  position: sticky;
  top: 0;
  z-index: 3;
  background: rgba(6, 18, 37, 0.72);
  backdrop-filter: blur(10px) saturate(1.2);
  box-shadow: 0 8px 20px rgba(2, 10, 24, 0.25);
  -webkit-overflow-scrolling: touch;
}

.sub-tabs button {
  background: var(--card);
  color: var(--muted);
  border: 1px solid rgba(73, 182, 255, 0.15);
  padding: 6px 14px;
  border-radius: 999px;
}

.sub-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pill-delete {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 92, 92, 0.5);
  background: rgba(8, 18, 33, 0.7);
  color: #ff8a8a;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}

.sub-tabs .active {
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #041327;
  border-color: transparent;
}

.items {
  padding: 0 16px;
  position: relative;
  z-index: 1;
}

.sub-section {
  scroll-margin-top: 70px;
}

.sub-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 10px 0 12px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #081a33;
  border: 1px solid rgba(73, 182, 255, 0.2);
  box-shadow: 0 6px 16px rgba(3, 12, 30, 0.45);
}

.sub-heading h2 {
  margin: 0;
  font-size: 16px;
  color: var(--text);
  letter-spacing: 0.6px;
  flex: 1;
}

.sub-order {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.sub-order-btn {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid rgba(73, 182, 255, 0.35);
  background: rgba(8, 18, 33, 0.75);
  color: var(--text);
  font-size: 14px;
  line-height: 20px;
  padding: 0;
  cursor: pointer;
}

.sub-order-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.empty {
  margin: 10px 0 30px;
}

.empty-card {
  background: linear-gradient(140deg, rgba(15, 47, 89, 0.85), rgba(6, 18, 37, 0.85));
  border: 1px dashed rgba(73, 182, 255, 0.25);
  border-radius: 18px;
  padding: 20px;
  text-align: center;
  color: var(--muted);
}

.empty-mark {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  margin: 0 auto 12px;
  background: radial-gradient(circle at 30% 30%, rgba(73, 182, 255, 0.5), transparent 60%);
  border: 1px solid rgba(73, 182, 255, 0.35);
}

.empty-card h3 {
  margin: 0 0 6px;
  color: var(--text);
  font-size: 16px;
}

.empty-card p {
  margin: 0;
  font-size: 13px;
}

.item-card {
  background: linear-gradient(135deg, rgba(18, 41, 74, 0.95), rgba(6, 17, 36, 0.96));
  border-radius: 18px;
  margin-bottom: 20px;
  border: 1px solid rgba(140, 196, 255, 0.23);
  box-shadow:
    0 12px 26px rgba(2, 10, 24, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    inset 0 -1px 0 rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 12px;
  position: relative;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
  overflow: hidden;
}

.item-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(140deg, rgba(73, 182, 255, 0.45), rgba(255, 178, 96, 0.2), rgba(255, 111, 111, 0.18));
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
  opacity: 0.7;
}

.item-card:hover {
  transform: translateY(-2px);
  border-color: rgba(169, 206, 255, 0.48);
  box-shadow:
    0 18px 34px rgba(2, 10, 24, 0.52),
    0 4px 10px rgba(0, 0, 0, 0.25);
}

.item-order {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 auto;
  justify-content: center;
}

.item-order-btn {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(140, 188, 255, 0.35);
  background: rgba(11, 24, 44, 0.85);
  color: var(--text);
  font-size: 14px;
  line-height: 22px;
  padding: 0;
  cursor: pointer;
}

.item-order-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.info {
  padding: 0;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: space-between;
}

.top {
  display: flex;
  justify-content: space-between;
  color: var(--text);
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 4px;
}

.top-title {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.top-title h3 {
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.top-title .spice {
  margin: 0;
}

.description {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0.15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spice {
  margin: 0;
  color: #ff1e1e;
  letter-spacing: 1px;
  font-size: 14px;
  white-space: nowrap;
}

.price-chip {
  background: rgba(124, 188, 255, 0.14);
  border: 1px solid rgba(124, 188, 255, 0.36);
  color: #b7e6ff;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.price-editing {
  position: relative;
  z-index: 5;
  background: linear-gradient(135deg, rgba(9, 16, 28, 0.98), rgba(3, 8, 14, 0.98));
  border: 1px solid rgba(124, 188, 255, 0.45);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
}

.price-editing .price-input,
.price-editing .price-save,
.price-editing .price-cancel {
  position: relative;
  z-index: 6;
}

.price-chip-editable {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.price-input {
  width: 74px;
  background: rgba(8, 18, 35, 0.9);
  color: var(--text);
  border: 1px solid rgba(124, 188, 255, 0.4);
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
  font-family: inherit;
  margin-right: 4px;
}

.price-save {
  margin-right: 4px;
  background: rgba(124, 188, 255, 0.14);
  color: #b7e6ff;
  border: 1px solid rgba(124, 188, 255, 0.36);
  border-radius: 999px;
  padding: 2px 7px;
  font-size: 11px;
  cursor: pointer;
}

.price-cancel {
  background: rgba(255, 138, 138, 0.12);
  color: #ffafaf;
  border: 1px solid rgba(255, 138, 138, 0.35);
  border-radius: 999px;
  padding: 2px 7px;
  font-size: 11px;
  cursor: pointer;
}

.price-edit-trigger {
  margin-left: 6px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: rgba(124, 188, 255, 0.18);
  color: var(--text);
  cursor: pointer;
  line-height: 16px;
}

.top .price-chip {
  color: #b7e6ff;
  font-weight: 700;
  text-shadow: none;
}

.item-edit-badge {
  position: absolute;
  top: 8px;
  right: 38px;
  z-index: 2;
  border: 1px solid rgba(124, 188, 255, 0.45);
  background: rgba(8, 18, 33, 0.78);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  cursor: pointer;
}

.thumb {
  width: 120px;
  height: 90px;
  flex: 0 0 120px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(8, 18, 35, 0.85);
  position: relative;
  border: 1px solid rgba(124, 188, 255, 0.25);
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.95) contrast(1.05) brightness(0.96);
}

.delete-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid rgba(255, 92, 92, 0.5);
  background: rgba(26, 12, 20, 0.8);
  color: #ff8a8a;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
}

.item-edit-overlay {
  position: fixed;
  inset: 0;
  z-index: 11;
  background: rgba(2, 8, 18, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.item-edit-panel {
  width: min(560px, 100%);
  background: linear-gradient(180deg, rgba(10, 24, 45, 0.98), rgba(6, 17, 36, 0.98));
  border: 1px solid rgba(124, 188, 255, 0.24);
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  padding: 18px;
}

.item-edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.item-edit-header h3 {
  margin: 0;
  color: var(--text);
  font-size: 18px;
}

.item-edit-close,
.item-edit-save,
.item-edit-cancel {
  border-radius: 12px;
  padding: 10px 14px;
  font-family: inherit;
  cursor: pointer;
}

.item-edit-close {
  border: 1px solid rgba(124, 188, 255, 0.24);
  background: transparent;
  color: var(--muted);
}

.item-edit-form {
  display: grid;
  gap: 10px;
}

.item-edit-form input,
.item-edit-form select {
  width: 100%;
  background: rgba(8, 18, 35, 0.92);
  color: var(--text);
  border: 1px solid rgba(124, 188, 255, 0.18);
  border-radius: 12px;
  padding: 11px 12px;
  font-family: inherit;
}

.item-edit-meta {
  color: var(--muted);
  font-size: 12px;
}

.item-edit-actions {
  display: flex;
  gap: 10px;
}

.item-edit-save {
  border: none;
  background: linear-gradient(135deg, var(--accent-strong), var(--accent));
  color: #041327;
  font-weight: 700;
}

.item-edit-cancel {
  border: 1px solid rgba(255, 138, 138, 0.24);
  background: transparent;
  color: #ffafaf;
}

.item-edit-save:disabled {
  opacity: 0.7;
  cursor: default;
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(2, 8, 18, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 10;
}

.lightbox-card {
  width: min(520px, 100%);
  background: var(--card-strong);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(73, 182, 255, 0.18);
  box-shadow: 0 20px 50px rgba(1, 8, 20, 0.6);
  position: relative;
}

.close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(6, 18, 37, 0.8);
  color: var(--text);
  border: 1px solid rgba(73, 182, 255, 0.2);
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 12px;
}

.lightbox-image {
  width: 100%;
  height: 260px;
  background: var(--card);
}

.lightbox-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lightbox-info {
  padding: 16px;
}

.lightbox-info h2 {
  margin: 0 0 6px;
  color: var(--text);
}

.lightbox-info p {
  margin: 0 0 12px;
  color: var(--muted);
  font-size: 14px;
}

.lightbox-info .spice {
  margin-top: 8px;
}

.price {
  color: var(--accent);
  font-weight: 700;
  font-size: 18px;
}
</style>
